/**
 * @file useSeoCardForm.ts
 * @description Правки одной карточки «Сайт → SEO → Основное» (phase19.16,
 * партия Р3в, v0.19.64; SB145, SB58, SEO14): поля карточки поверх ответа
 * сервера, «Сохранить» — сразу на сайт (только изменённые поля), «Отменить».
 *
 * Стоп-лист (SB58) проверяется при сохранении: 409 `site_seo_text_blocked` →
 * под кнопкой ошибки и галочка владельца, как в окне публикации (SB125), и
 * повторное сохранение с `acknowledgeChecks`. Предупреждения ответа: смена
 * заголовка или описания — «в поиске обновится за дни или недели»; код
 * подтверждения необычного вида — сохранён, но тост-предупреждение. Чужая
 * правка (409) — хук настроек перечитывает, правки карточки остаются.
 */

import { useState } from "react";

import type { ShopSeoApi } from "./useShopSeoSettings";
import { useNotify } from "../../components/ui/NotifyProvider";
import type { ShopSeoFieldKey, ShopSeoWarning } from "../../lib/api-site-seo";
import { errorCode, seoErrorText, stopListChecks } from "../../lib/site-seo/seo-errors";
import { SEARCH_UPDATE_NOTE, SEO_APPLY_NOTE, touchesSearch } from "../../lib/site-seo/seo-fields";
import { seoFormValues, seoPatchOf, seoValueChanged, type ShopSeoFormValues } from "../../lib/site-seo/seo-patch";
import type { PublishCheck } from "../../lib/site-view/types";

export interface SeoCardForm<K extends ShopSeoFieldKey> {
  values: Pick<ShopSeoFormValues, K>;
  set: <F extends K>(key: F, value: ShopSeoFormValues[F]) => void;
  changed: K[];
  dirty: boolean;
  saving: boolean;
  /** Ошибки стоп-листа последнего сохранения. */
  checks: PublishCheck[];
  ack: boolean;
  setAck: (value: boolean) => void;
  /** Ошибка формата кодов подтверждения — показывает карточка. */
  fieldError: { code: string; text: string } | null;
  save: () => Promise<boolean>;
  reset: () => void;
}

/** Текст тоста после сохранения по предупреждениям сервера и полям. */
export function savedText(warnings: readonly ShopSeoWarning[], changed: readonly ShopSeoFieldKey[]): string {
  const delay = warnings.some((w) => w.code === "site_seo_search_update_delay") || touchesSearch(changed);
  return delay ? `Сохранено. ${SEARCH_UPDATE_NOTE}` : `Сохранено. ${SEO_APPLY_NOTE}`;
}

const VERIFICATION_ERRORS: ReadonlySet<string> = new Set(["site_seo_verification_invalid", "site_seo_verification_engine", "site_seo_verification_limit"]);

export function useSeoCardForm<K extends ShopSeoFieldKey>(api: ShopSeoApi, keys: readonly K[]): SeoCardForm<K> {
  const notify = useNotify();
  const [edits, setEdits] = useState<Partial<Pick<ShopSeoFormValues, K>>>({});
  const [saving, setSaving] = useState(false);
  const [checks, setChecks] = useState<PublishCheck[]>([]);
  const [ack, setAck] = useState(false);
  const [fieldError, setFieldError] = useState<SeoCardForm<K>["fieldError"]>(null);
  const server = api.settings ? seoFormValues(api.settings) : null;

  const values = Object.fromEntries(keys.map((key) => [key, key in edits ? edits[key] : server?.[key]])) as Pick<ShopSeoFormValues, K>;
  const changed = server ? keys.filter((key) => key in edits && seoValueChanged(key, values[key], server[key])) : [];

  function set<F extends K>(key: F, value: ShopSeoFormValues[F]) {
    setEdits((prev) => ({ ...prev, [key]: value }));
    setFieldError(null);
    setChecks([]);
    setAck(false);
  }

  function reset() {
    setEdits({});
    setChecks([]);
    setAck(false);
    setFieldError(null);
  }

  async function save(): Promise<boolean> {
    if (changed.length === 0) return true;
    setSaving(true);
    try {
      const result = await api.save({ ...seoPatchOf(values, changed), ...(ack && checks.length > 0 ? { acknowledgeChecks: true } : {}) });
      reset();
      notify.success(savedText(result.warnings, changed));
      if (result.warnings.some((w) => w.code === "site_seo_verification_format")) {
        notify.warning("Код подтверждения сохранён, но выглядит необычно — проверьте, что скопировали его целиком.");
      }
      return true;
    } catch (err) {
      const found = stopListChecks(err);
      const code = errorCode(err);
      if (found.length > 0) {
        setChecks(found);
        setAck(false);
      } else if (code && VERIFICATION_ERRORS.has(code)) {
        setFieldError({ code, text: seoErrorText(err, "") });
      } else if (code !== "site_seo_version_conflict") {
        notify.error(seoErrorText(err, "Не удалось сохранить настройки SEO."));
      }
      return false;
    } finally {
      setSaving(false);
    }
  }

  return { values, set, changed, dirty: changed.length > 0, saving, checks, ack, setAck, fieldError, save, reset };
}
